/** WorkspaceCreatePage — page to spin up a new workspace/business. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useCreateWorkspace } from "@/entities/workspace";
import { clearGrowthReferral, createGrowthAttribution, readGrowthReferral } from "@/entities/growth-network";
import { ApiError } from "@/shared/api";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

export function WorkspaceCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const { mutateAsync, isPending } = useCreateWorkspace();

  const [displayName, setDisplayName] = useState("");
  const [slug, setSlug] = useState("");
  const [tagline, setTagline] = useState("");
  const [industry, setIndustry] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#4338CA");

  const handleNameChange = (v: string) => {
    setDisplayName(v);
    if (!slug || slug === toSlug(displayName)) setSlug(toSlug(v));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const workspace = await mutateAsync({
        slug: toSlug(slug || displayName),
        profile: {
          display_name: displayName,
          tagline: tagline || null,
          industry: industry || null,
          logo_key: null,
          primary_color: primaryColor,
          accent_color: null,
          timezone: "UTC",
          locale: "en-US",
          currency: "USD",
        },
      });
      const referralCode = readGrowthReferral();
      if (referralCode) {
        try {
          await createGrowthAttribution(workspace.id, referralCode);
          clearGrowthReferral();
          toast.success("Workspace created and invitation connected.");
        } catch (attributionError) {
          const detail = attributionError instanceof ApiError ? attributionError.message : "The invitation could not be connected.";
          toast.error(`Workspace created, but referral attribution needs attention: ${detail}`);
        }
      } else {
        toast.success("Workspace created");
      }
      navigate("/dashboard");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to create workspace";
      toast.error(msg);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-6 py-14">
      <Card
        data-testid="workspace-create-card"
        className="border-border/70 bg-card/70 backdrop-blur"
      >
        <CardHeader>
          <CardTitle className="font-display text-3xl font-semibold tracking-tight">
            New workspace
          </CardTitle>
          <CardDescription className="text-sm">
            A workspace is a living business. It owns its brand brain, campaigns, team, and every capability Orkexra grows into.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="display_name">Business name</Label>
              <Input
                id="display_name"
                data-testid="workspace-create-display-name"
                required
                value={displayName}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Acme Cleaning Co."
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="slug">URL slug</Label>
              <Input
                id="slug"
                data-testid="workspace-create-slug"
                required
                value={slug}
                onChange={(e) => setSlug(toSlug(e.target.value))}
                placeholder="acme-cleaning"
              />
              <p className="text-[11px] text-muted-foreground">
                Lowercase letters, digits, and hyphens. 2–40 characters. Must be globally unique.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tagline">Tagline</Label>
              <Input
                id="tagline"
                data-testid="workspace-create-tagline"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="One line that says who this business is."
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="industry">Industry</Label>
              <Input
                id="industry"
                data-testid="workspace-create-industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="Cleaning services"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="primary_color">Primary color</Label>
              <div className="flex items-center gap-3">
                <input
                  id="primary_color"
                  data-testid="workspace-create-primary-color"
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value.toUpperCase())}
                  className="h-10 w-14 cursor-pointer rounded-md border border-border bg-background p-1"
                />
                <code className="rounded bg-secondary px-2 py-1 font-mono text-xs">
                  {primaryColor}
                </code>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                data-testid="workspace-create-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isPending || !displayName || !slug}
                data-testid="workspace-create-submit"
              >
                {isPending ? "Creating…" : "Create workspace"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
